import React, { useEffect, useMemo, useState } from "react";
import { VALIDATION } from "../../constants";
import {
  HostFormState,
  buildHostPayload,
  hostFormHasChanges,
  validateHostForm,
  validateHostPhoto,
} from "../../payloads/buildHostPayload";
import { webinarService } from "../../services/webinarService";
import { WebinarServiceError } from "../../types/dto";
import { WebinarHost } from "../../types/models";
import { isValidPhone } from "../../utils/validatePhone";
import "./HostProfileModal.scss";

type Props = {
  isOpen: boolean;
  host?: WebinarHost | null;
  onClose: () => void;
  onSaved: (host: WebinarHost) => void;
};

const empty: HostFormState = {
  name: "",
  role: "",
  bio: "",
  creds: [""],
  photo: "",
  phoneNumber: "",
};

function formFromHost(host: WebinarHost): HostFormState {
  return {
    id: host.id,
    name: host.name,
    role: host.role,
    bio: host.bio,
    creds: host.creds.length ? [...host.creds] : [""],
    photo: host.photo || "",
    phoneNumber: host.phoneNumber || "",
  };
}

const HostProfileModal: React.FC<Props> = ({
  isOpen,
  host,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<HostFormState>(empty);
  const [baseline, setBaseline] = useState<HostFormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const isEdit = !!host;

  useEffect(() => {
    if (!isOpen) return;
    if (host) {
      const next = formFromHost(host);
      setForm(next);
      setBaseline(next);
    } else {
      setForm({ ...empty, creds: [""], phoneNumber: "" });
      setBaseline(null);
    }
    setError(null);
    setPhoneTouched(false);
    setUploading(false);
    setPhotoPreview(null);
  }, [isOpen, host]);

  const checks = useMemo(
    () => validateHostForm(form, { isEdit }),
    [form, isEdit],
  );

  const hasChanges = useMemo(() => {
    if (!isEdit || !baseline) return true;
    return hostFormHasChanges(form, baseline);
  }, [isEdit, baseline, form]);

  const canSave = checks.ok && hasChanges && !uploading && !saving;

  if (!isOpen) return null;

  const set = <K extends keyof HostFormState>(
    key: K,
    value: HostFormState[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const avatarSrc =
    photoPreview ||
    (/^https?:\/\//i.test(form.photo || "") ? form.photo : "");

  const heading = host ? `Editing ${host.name}` : "New host profile";
  const phoneShowError =
    !isEdit && phoneTouched && !isValidPhone(form.phoneNumber || "");

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || uploading) return;
    const err = validateHostPhoto(file);
    if (err) {
      setError(err);
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const uploaded = await webinarService.uploadHostImage(file);
      set("photo", uploaded.storageKey);
      setPhotoPreview(uploaded.previewUrl);
    } catch (err) {
      setError(
        err instanceof WebinarServiceError
          ? err.message
          : "Image upload failed",
      );
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setPhoneTouched(true);
    try {
      const payload = buildHostPayload(form, baseline);
      const saved = await webinarService.saveHost(payload);
      onSaved({
        ...saved,
        photo: saved.photo || photoPreview || undefined,
      });
      onClose();
    } catch (e) {
      setError(
        e instanceof WebinarServiceError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Could not save host",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wf-host-panel">
      <div className="wf-host-panel__head">
        <div className="wf-host-panel__heading">{heading}</div>
        <span className="wf-host-panel__sub">
          Renders on the session card, the host block and the invite
        </span>
      </div>

      <div className="wf-host-panel__photo-row">
        <div className="wf-host-panel__avatar-wrap">
          <div className="wf-avatar lg">
            {avatarSrc ? (
              <img src={avatarSrc} alt="" />
            ) : (
              <svg width="64" height="64" viewBox="0 0 64 64">
                <rect width="64" height="64" fill="#ECE7E1" />
                <circle cx="32" cy="24" r="11" fill="#A09890" />
                <path
                  d="M32 38c-11 0-19 7-21 17h42c-2-10-10-17-21-17z"
                  fill="#A09890"
                />
              </svg>
            )}
          </div>
          <div className="wf-host-panel__avatar-label">AVATAR</div>
        </div>
        <div className="wf-host-panel__photo-actions">
          <label className="wf-host-panel__upload-label">
            Profile picture{" "}
            {isEdit ? null : <span className="req">*</span>}
          </label>
          <div className="wf-host-panel__upload-row">
            <label
              className="wf-host-panel__upload-btn"
              style={{ opacity: uploading ? 0.6 : 1, pointerEvents: uploading ? "none" : undefined }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 16V4" />
                <path d="M7.5 8.5L12 4l4.5 4.5" />
                <path d="M4 15v3.5a1.5 1.5 0 001.5 1.5h13a1.5 1.5 0 001.5-1.5V15" />
              </svg>
              {uploading
                ? "Uploading…"
                : form.photo
                  ? "Replace photo"
                  : "Upload photo"}
              <input
                type="file"
                accept="image/jpeg,image/png"
                hidden
                disabled={uploading || saving}
                onChange={onUpload}
              />
            </label>
            {form.photo ? (
              <button
                type="button"
                className="wf-host-panel__remove-photo"
                disabled={uploading || saving}
                onClick={() => {
                  setPhotoPreview(null);
                  set("photo", "");
                }}
              >
                Remove
              </button>
            ) : null}
          </div>
          <div className="wf-host-panel__photo-note">
            {form.photo
              ? "Shows on the session card, the host block, the invite and the push."
              : isEdit
                ? "JPG or PNG up to 512KB; square crops best. With no upload, the host shows the default avatar."
                : "Required. JPG or PNG up to 512KB; square crops best."}
          </div>
        </div>
      </div>

      <div className="wf-host-panel__grid">
        <div>
          <label>
            Name <span className="req">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            placeholder="Dr. Meera Iyer"
            onChange={(e) => set("name", e.target.value)}
            style={{
              borderColor: checks.nameOk ? "#ECE7E1" : "#E8B3A8",
            }}
          />
        </div>
        <div>
          <label>
            Role <span className="req">*</span>
          </label>
          <input
            type="text"
            value={form.role}
            placeholder="Gynaecologist · PCOS"
            onChange={(e) => set("role", e.target.value)}
            style={{
              borderColor: checks.roleOk ? "#ECE7E1" : "#E8B3A8",
            }}
          />
        </div>
      </div>

      <div className="wf-host-panel__grid">
        <div>
          <label>Phone {isEdit ? null : <span className="req">*</span>}</label>
          <input
            type="tel"
            inputMode="numeric"
            value={form.phoneNumber || ""}
            placeholder="9876543210"
            disabled={isEdit}
            readOnly={isEdit}
            onChange={(e) => set("phoneNumber", e.target.value)}
            onBlur={() => setPhoneTouched(true)}
            maxLength={10}
            minLength={10}
            aria-invalid={phoneShowError}
            style={{
              borderColor:
                isEdit || checks.phoneOk || !phoneTouched
                  ? "#ECE7E1"
                  : "#E8B3A8",
            }}
          />
          {isEdit ? (
            <div className="wf-host-panel__field-hint">
              Phone number can’t be changed after create
            </div>
          ) : phoneShowError ? (
            <div className="wf-host-panel__field-hint wf-host-panel__field-hint--error">
              Use a valid Indian mobile (10 digits, starting 6–9). +91 allowed.
            </div>
          ) : null}
        </div>
      </div>

      <div className="wf-host-panel__bio">
        <div className="wf-host-panel__bio-head">
          <label>
            Bio {isEdit ? null : <span className="req">*</span>}
          </label>
          <span
            style={{
              color: checks.bioOk ? "#918A82" : "#B4483A",
            }}
          >
            {form.bio.length} / {VALIDATION.bioMax}
          </span>
        </div>
        <textarea
          rows={3}
          maxLength={VALIDATION.bioMax}
          value={form.bio}
          onChange={(e) => set("bio", e.target.value)}
          placeholder="Two or three sentences in the host’s voice."
          style={{
            borderColor: checks.bioOk ? undefined : "#E8B3A8",
          }}
        />
      </div>

      <div className="wf-host-panel__creds">
        <label>
          Educational Qualification / Credentials{" "}
          {isEdit ? null : <span className="req">*</span>}
        </label>
        <div className="wf-host-panel__cred-list">
          {form.creds.map((c, i) => (
            <div key={i} className="wf-host-panel__cred-chip">
              <input
                type="text"
                maxLength={VALIDATION.credCharMax}
                value={c}
                placeholder="e.g. MD Medicine"
                style={{
                  width: `${Math.max(110, Math.min(210, c.length * 7.4 + 30))}px`,
                }}
                onChange={(e) => {
                  const next = [...form.creds];
                  next[i] = e.target.value;
                  set("creds", next);
                }}
              />
              <button
                type="button"
                aria-label="Remove credential"
                onClick={() => {
                  const next = form.creds.filter((_, idx) => idx !== i);
                  set("creds", next.length ? next : [""]);
                }}
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            className="wf-host-panel__add-cred"
            style={{
              opacity: form.creds.length >= VALIDATION.credMax ? 0.4 : 1,
            }}
            disabled={form.creds.length >= VALIDATION.credMax}
            onClick={() => {
              if (form.creds.length >= VALIDATION.credMax) return;
              set("creds", [...form.creds, ""]);
            }}
          >
            + Add
          </button>
        </div>
      </div>

      {error ? <div className="wf-host-panel__error">{error}</div> : null}

      <div className="wf-host-panel__footer">
        <span
          className="wf-host-panel__note"
          style={{ color: canSave ? "#918A82" : "#B4483A" }}
        >
          {isEdit
            ? hasChanges
              ? "Saving updates every session hosted by this profile."
              : "Change something to save."
            : "Saving selects this host for the session."}
        </span>
        <div className="wf-host-panel__actions">
          <button
            type="button"
            className="wf-host-panel__cancel"
            disabled={uploading || saving}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="wf-host-panel__save"
            disabled={saving || uploading || !canSave}
            onClick={save}
            style={{
              background: canSave ? "#299D6B" : "#E4DED7",
              color: canSave ? "#FFFFFF" : "#A09890",
            }}
          >
            {saving ? "Saving…" : isEdit ? "Save profile" : "Create profile"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HostProfileModal;
